<template>

   <div class="mt-2 mb-2 text-center" v-if="currentDate">
      <h6 class="card-subtitle text-muted" style="margin-bottom: 0.5rem;">Date: <strong>{{ this.currentDate }}</strong></h6>
   </div>

   <form ref="blockFormRef" class="d-flex flex-column" style="gap: 0.5rem">
      <LoadingPage v-if="isLoading"></LoadingPage>
      <div>
         <label for="organisation">Block Name <span class="text-danger">*</span></label>
         <input type="text" id="blockName" v-model.trim="block.blockName" @blur="v$.block.blockName.$touch"
            class="form-control" placeholder="Block Name"
            :disabled="isShowContentType" />

         <span class="form-text text-danger" v-if="v$.block.blockName.$error">
            Block Name is required.
         </span>
      </div>

      <div>
         <label for="blockType">Block Type <span class="text-danger">*</span></label>
         <select class="form-select" id="blockType" v-model.trim="block.blockType" @blur="v$.block.blockType.$touch">
            <option selected disabled value>Please choose...</option>
            <option v-for="blockType in filteredBlockTypes" :value="blockType.id" :key="blockType.id">{{ blockType.display_name
            }}
            </option>
         </select>

         <span class="form-text text-danger" v-if="v$.block.blockType.$dirty && block.blockType == ''">
            Block Type is required.
         </span>
      </div>

      <div>
         <label for="blockUse">Block Use <span class="text-danger">*</span> </label>
         <select class="form-select" id="blockUse" v-model.trim="block.blockUse" @blur="v$.block.blockUse.$touch"
            :disabled="!!initialBlockUse">
            <option selected disabled value>Please choose...</option>
            <option v-for="bUse in blockUse" :value="bUse.id" :key="bUse.id">{{ bUse.display_name }}
            </option>
         </select>

         <span class="form-text text-danger" v-if="v$.block.blockUse.$dirty && block.blockUse == ''">
            Block Use is required.
         </span>
      </div>

      <div v-if="!isTestimonial" class="form-check d-flex align-items-center" style="margin-top: 5px;">
         <input class="form--check-input" type="checkbox" v-model="block.defaultVersion" id="defaultCheck1" checked>
         <label class="form--check-label mb-2" for="defaultCheck1">
            Default Version
         </label>
      </div>


      <!-- Multi-Product Selection (for ProductService block type) -->
      <div v-if="isShowProduct && !isTestimonial" style="position: relative;">
         <label>Products</label>

         <!-- Selected products shown as removable tags -->
         <div v-if="block.products && block.products.length > 0" class="selected-products-tags">
            <span v-for="(p, idx) in block.products" :key="'sel-' + p.id" class="product-tag">
               {{ p.display_name }}
               <span class="product-tag-remove" @click.stop="removeProduct(idx)">&times;</span>
            </span>
         </div>

         <div class="d-flex align-items-center" style="gap: 0.5rem">
            <input type="text" class="form-control"
               :value="block.products && block.products.length > 0 ? block.products.length + ' product(s) selected' : ''"
               readonly placeholder="No products selected" />
            <button type="button" class="browse-btn" @click.stop="showProductPopup = !showProductPopup">
               <i class="fa-solid fa-magnifying-glass"></i> Browse
            </button>
         </div>

         <!-- Inline Product Browser Panel -->
         <div v-if="showProductPopup" class="product-panel" @click.stop>
            <div class="product-panel-header">
               <span class="product-panel-title">Select Products</span>
               <span class="product-panel-close" @click.stop="closeProductPopup()">&times;</span>
            </div>
            <div class="product-panel-tabs">
               <div class="product-panel-tab" :class="{ active: productTab === 'auth' }"
                  @click.stop="productTab = 'auth'">Auth Products</div>
               <div class="product-panel-tab" :class="{ active: productTab === 'internal' }"
                  @click.stop="productTab = 'internal'">Internal Products</div>
            </div>
            <div class="product-panel-search">
               <input type="text" v-model="productSearchQuery" placeholder="Search products..." />
            </div>
            <div class="product-panel-list">
               <template v-if="productTab === 'auth'">
                  <div v-for="item in filteredAuthProducts" :key="'auth-' + item.id"
                     class="product-panel-row" :class="{ 'product-panel-row-selected': isProductSelected(item) }"
                     @click.stop="toggleProduct(item)">
                     <div class="product-panel-row-badge">{{ item.id }}</div>
                     <div class="product-panel-row-info">
                        <div class="product-panel-row-name">{{ item.display_name }}</div>
                     </div>
                     <div class="product-panel-row-action">
                        {{ isProductSelected(item) ? '✓ Selected' : 'Select' }}
                     </div>
                  </div>
                  <div v-if="filteredAuthProducts.length === 0" class="product-panel-empty">No auth products found</div>
               </template>
               <template v-if="productTab === 'internal'">
                  <div v-for="item in filteredInternalProducts" :key="'int-' + item.id"
                     class="product-panel-row" :class="{ 'product-panel-row-selected': isProductSelected(item) }"
                     @click.stop="toggleProduct(item)">
                     <div class="product-panel-row-badge uuid">{{ item.id.substring(0, 8) }}...</div>
                     <div class="product-panel-row-info">
                        <div class="product-panel-row-name">{{ item.display_name }}</div>
                     </div>
                     <div class="product-panel-row-action">
                        {{ isProductSelected(item) ? '✓ Selected' : 'Select' }}
                     </div>
                  </div>
                  <div v-if="filteredInternalProducts.length === 0" class="product-panel-empty">No internal products found</div>
               </template>
            </div>
         </div>
      </div>

      <!-- Testimonial Multi-Product Selection (keeps VueMultiselect) -->
      <div v-if="isShowProduct && isTestimonial">
         <label for="products">Products</label>
         <VueMultiselect id="products" v-model="block.products" :options="productList" :multiple="true"
            open-direction="top" :close-on-select="true" label="display_name" track-by="id" :searchable="false"
            placeholder="Please choose at least one...">
         </VueMultiselect>
      </div>

      <div v-if="isShowContentType">
         <label for="products">Content Types <span class="text-danger">*</span></label>
         <VueMultiselect id="products" v-model="block.contentType" :options="filteredContentTypes" :multiple="false"
            open-direction="top" :close-on-select="true" label="display_name" track-by="id" :searchable="false"
            placeholder="Please choose one...">
         </VueMultiselect>
      </div>


      <div>
         <label for="color_header">Header Color</label>
         <div class="color-swatch-row">
            <button v-if="!presetColors.some(c => c.hex === block.color)" type="button"
               class="color-swatch color-swatch-active"
               :style="{ backgroundColor: block.color }"
               title="Current Color">
               <i class="fa-solid fa-check" style="font-size:10px;color:#fff;text-shadow:0 0 2px rgba(0,0,0,0.5);"></i>
            </button>
            <button v-for="c in presetColors" :key="c.hex" type="button"
               class="color-swatch" :class="{ 'color-swatch-active': block.color === c.hex }"
               :style="{ backgroundColor: c.hex }"
               :title="c.label"
               @click.stop="block.color = c.hex">
               <i v-if="block.color === c.hex" class="fa-solid fa-check" style="font-size:10px;color:#fff;text-shadow:0 0 2px rgba(0,0,0,0.5);"></i>
            </button>
            <button type="button" class="color-swatch color-swatch-custom" data-bs-toggle="popover"
               @click="toggleColorPicker($event, block.id, block.color, 'circle')"
               title="Custom Color">
               <i class="fa-solid fa-palette" style="font-size:12px;color:#435666;"></i>
            </button>
         </div>
         <div class="color-preview-strip" :style="{ backgroundColor: block.color }">
            <span class="color-preview-label">{{ block.color }}</span>
         </div>
      </div>

      <div id="popover" class="d-none" role="tooltip">
         <ColorPicker ref="colorPickerPopoverRef" @onColorChange="onColorChange" />
      </div>


      <div class="pt-4 pb-2 justify-content-center d-flex" style="gap:1rem">
         <button type="button" class="block-btn-clear" @click="resetForm();">Clear fields</button>
         <button type="button" class="block-btn-create" @click="createBlock()" :disabled="disabledWhenClicked">Create</button>
      </div>
   </form>

   <ConfirmModal
      v-model="showConfirmModal"
      :title="confirmModalTitle"
      :message="confirmModalMessage"
      @confirm="onConfirmAction"
   />

</template>

<script>
import moment from "moment";
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { log } from '@/plugins/helper.js'
import 'vue-tel-input/dist/vue-tel-input.css'
import VueMultiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import "@/assets/scss/vue-multiselect.scss";
import ColorPicker from '@/components/Utilities/color-picker.vue'
import LoadingPage from '@/components/Utilities/vertex-loading.vue'

//const _ = require('lodash');

export default {

   components: {
      VueMultiselect,
      ColorPicker,
      LoadingPage,
   },

   setup() {
      return { v$: useVuelidate() }
   },

   emits: ['onSubmit', 'onDelete'],

   props: {
      item: {
         type: Object,
         // required: true,
      },
      initialBlockUse: {
         type: String,
         default: ''
      }
   },

   data() {
      return {
         currentDate: null,
         block: {
            dateCreated: null,
            blockName: '',
            blockType: '',
            blockUse: '',
            defaultVersion: true,
            color: "#4682B4",
            product: null,
            products: [],
            content: "",
            contentType: null,
         },
         blockTypes: [],
         blockTypeFiltered: [],
         blockUse: [],
         productList: [],
         contentTypes: [],
         isEditing: false,
         disabledWhenClicked: false,
         isLoading: false,
         isShowProduct: false,
         isTestimonial: false,
         isShowContentType: false,
         productServiceBlockTypeID: null,
         testimonialBlockTypeID: null,
         contentID: null,
         chosenBlockType: "",
         // Product popup
         showProductPopup: false,
         productTab: 'auth',
         productSearchQuery: '',
         authProducts: [],
         internalProducts: [],
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingDeleteId: null,
      }
   },

   validations() {
      return {
         block: {
            blockName: { required },
            blockType: { required },
            blockUse: { required },
         },
      }
   },

   computed: {
      presetColors() {
         return [
            { hex: '#00A0C1', label: 'Vertex Cyan' },
            { hex: '#4682B4', label: 'Steel Blue' },
            { hex: '#26a59a', label: 'Teal' },
            { hex: '#4cb050', label: 'Green' },
            { hex: '#ffa727', label: 'Orange' },
            { hex: '#eb4165', label: 'Red Pink' },
            { hex: '#8e24aa', label: 'Purple' },
            { hex: '#aa47bc', label: 'Lilac' },
            { hex: '#ffeb3c', label: 'Yellow' },
            { hex: '#435666', label: 'Dark Grey' },
         ]
      },
      blockTypeColorMap() {
         return {
            'CoverPage':              '#4682B4',
            'ProductService':         '#26a59a',
            'PricingTable':           '#ffeb3c',
            'Image':                  '#ffeb3c',
            'Content':                '#eb4165',
            'MasterTerms':            '#ffa727',
            'ServiceModuleStandard':  '#ffa727',
            'ServiceModuleTerm':      '#ffa727',
            'ServiceModuleSubscription': '#ffa727',
            'ScheduleTable':          '#aa47bc',
            'ExecutionPage':          '#4cb050',
            'Testimonial':            '#00A0C1',
         }
      },
      proposalBlockTypes() {
         return ['CoverPage', 'ProductService', 'PricingTable', 'Image', 'Content']
      },
      contractBlockTypes() {
         return ['MasterTerms', 'ServiceModuleStandard', 'ServiceModuleTerm',
                 'ServiceModuleSubscription', 'ScheduleTable', 'ExecutionPage']
      },
      filteredBlockTypes() {
         if (!this.initialBlockUse) {
            return this.blockTypes
         }
         const useType = this.initialBlockUse.toLowerCase()
         if (useType === 'contract') {
            return this.blockTypes.filter(bt => this.contractBlockTypes.includes(bt.name))
         } else if (useType === 'proposal') {
            return this.blockTypes.filter(bt => this.proposalBlockTypes.includes(bt.name))
         }
         return this.blockTypes
      },
      allProductOptions() {
         // Combine auth + internal products into one list for the multiselect
         const combined = [...this.authProducts, ...this.internalProducts]
         // Deduplicate by id
         const seen = new Set()
         return combined.filter(p => {
            if (seen.has(p.id)) return false
            seen.add(p.id)
            return true
         })
      },
      filteredAuthProducts() {
         const q = this.productSearchQuery.toLowerCase()
         return this.authProducts.filter(p => p.display_name.toLowerCase().includes(q))
      },
      filteredContentTypes() {
         const selectedUse = this.blockUse.find(b => b.id === this.block.blockUse);
         const isProposal = selectedUse && selectedUse.name.toLowerCase() === 'proposal';
         if (isProposal) {
            const hideForProposal = ['ConflictsOfInterest', 'Insurances', 'conflicts_of_interest', 'insurances', 'WHSResponsibilities', 'whs_responsibilities'];
            return this.contentTypes.filter(ct => {
               // Filter by name if available, otherwise by display_name
               const ctName = ct.name || '';
               const ctDisplay = (ct.display_name || '').toLowerCase();
               return !hideForProposal.includes(ctName) && ctDisplay !== 'conflicts of interest' && ctDisplay !== 'insurances' && !ctDisplay.includes('whs') && !ctDisplay.includes('workplace health');
            });
         }
         return this.contentTypes;
      },
      filteredInternalProducts() {
         const q = this.productSearchQuery.toLowerCase()
         return this.internalProducts.filter(p => p.display_name.toLowerCase().includes(q))
      },
   },

   watch: {
      initialBlockUse(newVal) {
         log(newVal)
         this.matchBlockUseToProp();
      },
      'block.blockType'(newVal) {
         // Guard: only compare when IDs are actually loaded (not null)
         if (newVal && this.productServiceBlockTypeID && newVal === this.productServiceBlockTypeID) {
            this.isShowProduct = true;
            this.isShowContentType = false;
            this.isTestimonial = false;
            this.block.blockName = ""
         } else if (newVal && this.testimonialBlockTypeID && newVal === this.testimonialBlockTypeID) {
            this.isShowProduct = true;
            this.isShowContentType = false;
            this.isTestimonial = true;
            this.block.blockName = ""
         } else if (newVal && this.contentID && newVal === this.contentID) {
            this.isShowContentType = true;
            this.isShowProduct = false;
            this.isTestimonial = false;
            this.block.blockName = ""
         } else {
            this.isShowProduct = false;
            this.isShowContentType = false;
            this.isTestimonial = false;
            this.block.products = null
            this.block.contentType = null
            this.block.blockName = ""
         }

         // Auto-set header color based on block type
         const selectedType = this.blockTypes.find(bt => bt.id === newVal)
         if (selectedType && this.blockTypeColorMap[selectedType.name]) {
            this.block.color = this.blockTypeColorMap[selectedType.name]
         }

         this.block.product = null
         this.showProductPopup = false
      },
      'block.product'(newAddedVal) {
         // Only auto-name for non-ProductService blocks (ProductService uses multi-products with manual naming)
         if (this.productServiceBlockTypeID && this.block.blockType === this.productServiceBlockTypeID) {
            return
         }
         if (newAddedVal != undefined && newAddedVal != null & newAddedVal.display_name != "") {
            this.block.blockName = newAddedVal.display_name
         } else {
            this.block.blockName = ""
         }
      },
      'block.contentType'(newAddedVal) {
         if (newAddedVal != undefined && newAddedVal != null & newAddedVal.display_name != "") {
            this.block.blockName = newAddedVal.display_name
         } else {
            this.block.blockName = ""
         }
      }
   },

   created() {
      this.init()
   },

   mounted() {
   },

   methods: {
      init() {
         this.currentDate = moment(new Date()).format("DD-MMM-YYYY");
         this.getProductList()
         this.getBlockTypes()
         this.getBlockUse()
         this.getContentTypes()
         this.getAuthProductsList()
         this.getInternalProductsList()
      },

      matchBlockUseToProp() {
         if (!this.initialBlockUse || this.blockUse.length === 0) return;

         const found = this.blockUse.find(b =>
            b.name.toLowerCase() === this.initialBlockUse.toLowerCase() ||
            b.display_name.toLowerCase() === this.initialBlockUse.toLowerCase()
         );

         if (found) {
            this.block.blockUse = found.id;
         }
      },

      getInitialContent(blockType) {
         if (blockType == "Pricing Table") {
            return `
              <div id="pricing_table_block_wrapper" class="pricing-table-container" contenteditable="false" style="user-select: none;">
               
               <section id='pricing_table_intro_section' class='non-editable-section' style="margin-bottom: 15px;"> 
                  <p style='text-align:center; color: #00A0C1; font-weight: bold; margin-bottom: 5px; cursor: default;' id='pricing_table_intro_label'>
                     Pricing Table Introduction
                  </p>
                  <div id='start_pricing_table_intro' class='editable-content-inside' contenteditable="true" style="user-select: text; min-height: 50px; padding: 10px; border: 1px dashed #ccc; background-color: #fff; display: block; outline: none;">
                     <p><em>[Enter your introductory text for the pricing table here...]</em></p>
                  </div>
               </section>
               
               <hr style="border-top: 1px solid #eee; margin: 20px 0;">

               <section id='pricing_table_main_section' class='non-editable-section' style="margin-bottom: 15px;"> 
                  <p style='text-align:center; color: #00A0C1; font-weight: bold; margin-bottom: 5px; cursor: default;' id='pricing_table_main_label'>
                     Automated Pricing Table
                  </p>
                  <div id='start_pricing_table_main' class='non-editable-section' style="background-color: #f9f9f9; padding: 30px; text-align: center; border: 1px solid #ddd; border-radius: 4px;">
                     <h3 style="color: #435666; margin: 0; font-family: sans-serif;">[Automated Pricing Table Placeholder]</h3>
                     <p style="font-size: 13px; color: #666; font-family: sans-serif; margin-top: 5px;">The selected products and calculations will be automatically generated and rendered here.</p>
                  </div>
               </section>
               
               <hr style="border-top: 1px solid #eee; margin: 20px 0;">

               <section id='pricing_table_notes_section' class='non-editable-section' style="margin-bottom: 15px;"> 
                  <p style='text-align:center; color: #00A0C1; font-weight: bold; margin-bottom: 5px; cursor: default;' id='pricing_table_notes_label'>
                     Pricing Table Notes & Conclusion
                  </p>
                  <div id='start_pricing_table_notes' class='editable-content-inside' contenteditable="true" style="user-select: text; min-height: 50px; padding: 10px; border: 1px dashed #ccc; background-color: #fff; display: block; outline: none;">
                     <p><em>[Enter any concluding remarks or notes below the pricing table here...]</em></p>
                  </div>
               </section>

            </div>`
         }

         if (blockType == "Organisation Details") {
            return `
            <table style="border-collapse: collapse; width: 100%;" border="1"><colgroup><col style="width: 50%;"><col style="width: 50%;"></colgroup>
               <tbody>
                  <tr>
                     <td>Organisation Name</td>
                     <td>&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_organisation_name">organisation_name</span><span style="color: red;">*!</span>}}</mark>&nbsp;</td>
                  </tr>
                  <tr>
                     <td>ABN</td>
                     <td>&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_abn">abn</span><span style="color: red;">*!</span>}}</mark>&nbsp;</td>
                  </tr>
                  <tr>
                     <td>Physical Address</td>
                     <td>&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_org_address">org_address</span><span style="color: red;">*!</span>}}</mark>&nbsp;</td>
                  </tr>
                  <tr>
                     <td>Billing Address</td>
                     <td>&nbsp;</td>
                  </tr>
                  <tr>
                     <td>Business Phone</td>
                     <td>&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_contact_number">contact_number</span><span style="color: red;">*!</span>}}</mark>&nbsp;</td>
                  </tr>
                  <tr>
                     <td>Primary Contact</td>
                     <td>&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_first_name">first_name</span><span style="color: red;">*!</span>}} &nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_last_name">last_name</span><span style="color: red;">*!</span>}}</mark>&nbsp;</mark></td>
                  </tr>
                  <tr>
                     <td>Primary Contact Email</td>
                     <td>&nbsp;</td>
                  </tr>
                  <tr>
                     <td>Primary Contact Phone</td>
                     <td>&nbsp;</td>
                  </tr>
                  <tr>
                     <td>Secondary Contact</td>
                     <td>&nbsp;</td>
                  </tr>
                  <tr>
                     <td>Secondary Contact Email</td>
                     <td>&nbsp;</td>
                  </tr>
                  <tr>
                     <td>Secondary Contact Phone</td>
                     <td>&nbsp;</td>
                  </tr>
                  <tr>
                     <td>Billing Contact Name</td>
                     <td>&nbsp;</td>
                  </tr>
                  <tr>
                     <td>Billing Contact Email</td>
                     <td>&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_billing_email">billing_email</span><span style="color: red;">*!</span>}}</mark>&nbsp;</td>
                  </tr>
               </tbody>
            </table>
          `}
         if (blockType == "Testimonial") {
            return `
             <div><div>
            `
         }

         if (blockType == "Execution Page") {
            return `
<div id="execution_page_block">
  <h2 style="font-family:'Roboto',sans-serif;color:#00a0c1;font-size:20px;margin-bottom:12px;">Execution</h2>
  <p style="font-family:'Roboto',sans-serif;font-size:14px;line-height:1.7;margin-bottom:20px;">
    Executed as an agreement in accordance with section 127 of the Corporations Act 2001 (Cth).
  </p>

  <table style="width:100%;border-collapse:collapse;font-family:'Roboto',sans-serif;font-size:13px;">
    <tbody>
      <tr>
        <td style="width:50%;padding:20px;vertical-align:top;border:1px solid #ddd;">
          <p style="margin:0 0 4px;font-weight:600;">SIGNED for and on behalf of</p>
          <p style="margin:0 0 16px;color:#00a0c1;font-weight:700;">VERTEX CYBER SECURITY PTY LTD</p>
          <p style="margin:0 0 16px;">&nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_internal_xodo_sign_placement">internal_xodo_sign_placement</span><span style="color:red;">*!</span>}}</mark></p>
        </td>
        <td style="width:50%;padding:20px;vertical-align:top;border:1px solid #ddd;">
          <p style="margin:0 0 4px;font-weight:600;">SIGNED for and on behalf of</p>
          <p style="margin:0 0 16px;color:#00a0c1;font-weight:700;">&nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_organisation_name">organisation_name</span><span style="color:red;">*!</span>}}</mark></p>
          <p style="margin:0 0 16px;">&nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_external_xodo_sign_placement">external_xodo_sign_placement</span><span style="color:red;">*!</span>}}</mark></p>
        </td>
      </tr>
    </tbody>
  </table>
</div>`
         }

         if (blockType == "Schedule") {
            return `
<div id="schedule_table_block">
  <h2 style="font-family:'Roboto',sans-serif;color:#00a0c1;font-size:20px;margin-bottom:12px;">Schedule</h2>

  <table style="width:100%;border-collapse:collapse;font-family:'Roboto',sans-serif;font-size:13px;margin-bottom:24px;">
    <thead>
      <tr style="background-color:#00a0c1;color:#fff;">
        <th style="padding:10px 12px;text-align:left;">Item</th>
        <th style="padding:10px 12px;text-align:left;">Field</th>
        <th style="padding:10px 12px;text-align:left;">Details</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;font-weight:600;">Item 1</td>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;font-weight:600;">Parties</td>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;">
          <strong>Vertex Cyber Security Pty Ltd</strong> (ACN 606 831 099)<br>
          Suite 10, 30 Atchison Street, St Leonards NSW 2065<br><br>
          &nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_organisation_name">organisation_name</span><span style="color:red;">*!</span>}}</mark><br>
          &nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_org_address">org_address</span><span style="color:red;">*!</span>}}</mark><br>
          &nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_org_phone_number">org_phone_number</span><span style="color:red;">*!</span>}}</mark><br>
          ABN:&nbsp;&nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_abn">abn</span><span style="color:red;">*!</span>}}</mark>
        </td>
      </tr>
      <tr>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;font-weight:600;">Item 2</td>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;font-weight:600;">Selected Services &amp; Applicable Modules</td>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;">
          &nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_contract_pricing_table">contract_pricing_table</span><span style="color:red;">*!</span>}}</mark>
        </td>
      </tr>
      <tr>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;font-weight:600;">Item 3</td>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;font-weight:600;">Reference Documents</td>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;">
          <p style="margin:0 0 6px;"><strong>Date of Proposal:</strong>&nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_date_proposal_sent">date_proposal_sent</span><span style="color:red;">*!</span>}}</mark></p>
          <p style="margin:0 0 6px;"><strong>Proposal Link:</strong>&nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_proposal_link">proposal_link</span><span style="color:red;">*!</span>}}</mark></p>
          <p style="margin:0;"><strong>Termination Date (if applicable):</strong>&nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_termination_date">termination_date</span><span style="color:red;">*!</span>}}</mark></p>
          <mark>{{<span style="color:red;">!*</span><span id="variable_value_subscription_termination_notice">subscription_termination_notice</span><span style="color:red;">*!</span>}}</mark>
        </td>
      </tr>
      <tr>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;font-weight:600;">Item 4</td>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;font-weight:600;">Address for Service of Notices</td>
        <td style="padding:10px 12px;vertical-align:top;border-bottom:1px solid #e0e0e0;">
          <p style="margin:0 0 10px;"><strong>Vertex:</strong><br>
          Attention: Melanie Boyd<br>
          Postal Address: Suite 10, 30 Atchison Street, St Leonards NSW 2065 Australia<br>
          Email: melanie@vertexcybersecurity.com.au</p>
          <p style="margin:0;"><strong>Client:</strong><br>
          Attention:&nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_signers_names">signers_names</span><span style="color:red;">*!</span>}}</mark><br>
          Email:&nbsp;<mark>{{<span style="color:red;">!*</span><span id="variable_value_billing_email">billing_email</span><span style="color:red;">*!</span>}}</mark></p>
        </td>
      </tr>
    </tbody>
  </table>
</div>`
         }

         return ""
      },

      getDisplayName(blockTypeId) {
         let display_name_return = ""
         this.blockTypeFiltered.forEach((btf) => {
            if (btf.id == blockTypeId) {
               display_name_return = btf.display_name
            }
         })

         return display_name_return
      },

      toggleColorPicker(e, color, shape) {

         let element = e.target
         if (e.target) {
            element = e.target.closest('[data-bs-toggle="popover"]')

         }

         if (this.$refs.colorPickerPopoverRef != null) {
            this.$refs.colorPickerPopoverRef.showPopover(element, color, shape);
         }
      },

      onColorChange(colorCode) {
         this.block.color = colorCode
      },

      isCreate() {
         this.isEditing = false
         this.setDefaultCountryCode()
      },

      isEdit(id) {
         this.isEditing = true
         this.getLead(id)

         // Keep track of the original data
         this._beforeEditingCache = Object.assign({}, this.lead);
      },

      isDelete(id) {
         this.deleteBlock(id)
      },

      async deleteBlock(id) {
         this.pendingDeleteId = id;
         this.confirmModalTitle = 'Delete Block';
         this.confirmModalMessage = 'Do you want to delete this block?';
         this.showConfirmModal = true;
      },

      async onConfirmAction() {
         const id = this.pendingDeleteId;
         this.pendingDeleteId = null;
         if (!id) return;
         await this.$axios.delete("/blocks/" + id)
            .then((response) => {
               if (response.data.success) {
                  this.$toast.success(response.data.message)
                  this.$emit('onDelete')
               } else {
                  this.$toast.error(response.data.message)
               }
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      resetForm() {
         this.v$.$reset()
         this.block.blockName = ""
         this.block.blockType = ""
         this.block.blockUse = ""
         this.matchBlockUseToProp();
         this.block.defaultVersion = true
         this.block.product = null
         this.block.products = []
         this.block.contentType = null
         this.block.color = "#4682B4"
         this.isShowProduct = false
         this.isTestimonial = false
         this.isShowContentType = false
         this.showProductPopup = false
         this.productSearchQuery = ''
      },

      disableFormButton() {
         if (this.v$.$invalid || this.v$.$error ||
            this.block.blockType == '' || this.block.blockUse == '') {
            return true
         }
         return false
      },

      async createBlock() {

         this.v$.$validate();

         if (this.v$.$error) {
            return;
         }

         this.disabledWhenClicked = true;
         this.isLoading = true;

         let block = {
            block_name: this.block.blockName,
            block_type_id: this.block.blockType,
            block_use_id: this.block.blockUse,
            default_version: this.block.defaultVersion,
            block_hex_color: this.block.color,
            content: "", // Backend populates initial content based on block type
            content_type_id: (this.block.contentType != undefined && this.block.contentType.id != "") ? this.block.contentType.id : null
         }

         log(block)

         block.products = []

         // ProductService: use the multi-select products array
         if (this.productServiceBlockTypeID == this.block.blockType) {
            if (this.block.products != undefined && this.block.products != null && this.block.products.length > 0) {
               this.block.products.forEach((p) => {
                  block.products.push({
                     product_id: p.id.toString()
                  })
               })
            }
         }
         // Legacy single-product for other block types (not Content)
         else if (this.contentID !== this.block.blockType) {
            if (this.block.product != undefined && this.block.product != null) {
               if (this.block.product.id) {
                  block.products.push({
                     product_id: this.block.product.id.toString()
                  })
               }
            }
         }

         // Testimonial: use the multi-select products array
         if (this.testimonialBlockTypeID == this.block.blockType) {
            block.products = []

            if (this.block.products != undefined && this.block.products != null) {

               if (this.block.products.length <= 0) {
                  this.disabledWhenClicked = false;
                  this.$toast.error("Please choose at least one product.")
                  this.isLoading = false
                  return
               }

               this.block.products.forEach((p) => {
                  block.products.push({
                     product_id: p.id.toString()
                  })
               })
            } else {
               this.disabledWhenClicked = false;
               this.$toast.error("Please choose at least one product.")
               this.isLoading = false
               return
            }
         }


         if (this.contentID == this.block.blockType) {
            if (this.block.contentType == undefined || this.block.contentType.id == "") {
               this.disabledWhenClicked = false;
               this.$toast.error("Please choose a content type")
               this.isLoading = false;
               return
            }
         }

         await this.$axios.post("/blocks", block)
            .then((response) => {
               if (response.data.success) {
                  this.$toast.success(response.data.message)
                  this.resetForm();
                  this.$emit('onSubmit')
                  this.disabledWhenClicked = false;
                  this.isLoading = false;
               } else {
                  this.$toast.error(response.data.message)
                  this.disabledWhenClicked = false;
                  this.isLoading = false;
               }
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
               this.disabledWhenClicked = false;
               this.isLoading = false;
            })
         this.resetForm();
      },

      async getProductList() {
         await this.$axios.get("/categories?name=Products")
            .then((response) => {
               this.productList = []
               response.data.category.subcategories.forEach((p) => {
                  this.productList.push({
                     id: p.id,
                     display_name: p.display_name
                  })
               })

            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getAuthProductsList() {
         await this.$axios.get("/auth-products")
            .then((response) => {
               this.authProducts = []
               const items = response.data.authProducts || []
               items.forEach((item) => {
                  if (item.Product) {
                     this.authProducts.push({
                        id: item.Product.id,
                        display_name: item.Product.display_name || 'Unnamed',
                     })
                  }
               })
            })
            .catch((error) => {
               console.error('Failed to fetch auth products', error)
            })
      },

      async getInternalProductsList() {
         await this.$axios.get("/get-product-pricing")
            .then((response) => {
               this.internalProducts = []
               const items = response.data.productPrices || []
               items.forEach((item) => {
                  if (this.isUUID(item.ProductID)) {
                     this.internalProducts.push({
                        id: item.ProductID,
                        display_name: item.DisplayName || 'Unnamed',
                     })
                  }
               })
            })
            .catch((error) => {
               console.error('Failed to fetch internal products', error)
            })
      },

      toggleProduct(item) {
         if (!this.block.products) {
            this.block.products = []
         }
         const idx = this.block.products.findIndex(p => p.id === item.id)
         if (idx >= 0) {
            // Already selected — remove it
            this.block.products.splice(idx, 1)
         } else {
            // Not selected — add it
            this.block.products.push({
               id: item.id,
               display_name: item.display_name
            })
         }
      },

      isProductSelected(item) {
         if (!this.block.products) return false
         return this.block.products.some(p => p.id === item.id)
      },

      removeProduct(index) {
         this.block.products.splice(index, 1)
      },

      closeProductPopup() {
         setTimeout(() => {
            this.showProductPopup = false
         }, 150)
      },

      isUUID(val) {
         if (typeof val !== 'string') return false
         if (val.length !== 36) return false
         if (val[8] !== '-' || val[13] !== '-' || val[18] !== '-' || val[23] !== '-') return false
         return true
      },

      async getContentTypes() {
         await this.$axios.get("/categories?name=ContentType")
            .then((response) => {
               this.contentTypes = []
               response.data.category.subcategories.forEach((p) => {
                  this.contentTypes.push({
                     id: p.id,
                     display_name: p.display_name
                  })
               })

            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getBlockTypes() {
         await this.$axios.get("/categories?name=BlockType")
            .then((response) => {
               
               this.blockTypes = response.data.category.subcategories.filter(bt => bt.name !== "Testimonial");

               if (this.blockTypes != undefined && this.blockTypes != null && this.blockTypes.length > 0) {
                  this.blockTypes.forEach((bt) => {
                     if (bt.name == "ProductService") {
                        this.productServiceBlockTypeID = bt.id
                     }

                     if (bt.name == "Testimonial") {
                        this.testimonialBlockTypeID = bt.id
                     }

                     if (bt.name == "Content") {
                        this.contentID = bt.id
                     }

                     this.blockTypeFiltered.push({
                        "id": bt.id,
                        "display_name": bt.display_name
                     })
                  })
               }
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getBlockUse() {
         await this.$axios.get("/categories?name=BlockUse")
            .then((response) => {
               this.blockUse = response.data.category.subcategories
               this.matchBlockUseToProp();
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },
   },
};

</script>

<style scoped>
.high {
   color: #EB1C24;
}

.low {
   color: #00A54F;
}

.medium {
   color: #F3994D;
}

select>option {
   color: #435666;
}

.btn {
   width: 7rem;
}

form label:not(#isCharityLbl) {
   color: #00A0C1;
   margin: 0.5rem 0;
}

input:not([type="checkbox"]),
select {
   height: 40px;
   border-radius: 10px;
   border: 1px solid #D9D9D9;
   color: #435666;
}

.form--check-label {
   color: #495057 !important;
   font-size: 15px;
   align-items: center;
   justify-content: center;
}

.form--check-input {
   width: 20px;
   height: 20px;
   margin-left: -17px;
   margin-right: 10px;
   background-color: #00A0C1;
}

input[type=checkbox] {
   accent-color: #00A0C1;
}

.add-space {
   margin-bottom: 225px;
}

.btn-circle-color {
   width: 40px;
   height: 40px;
   padding: 6px 0px;
   border-radius: 15px;
   text-align: center;
   font-size: 12px;
   line-height: 1.42857;
   border: 1px solid #00A0C1;
   color: #00A0C1;
}

/* ===== Browse Button (exempt from .btn width rule) ===== */
.browse-btn {
   white-space: nowrap;
   border-radius: 8px !important;
   border: 1px solid #00A0C1;
   background: #fff;
   color: #00A0C1;
   padding: 6px 14px;
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;
   transition: all 0.2s;
   width: auto !important;
}

.browse-btn:hover {
   background: #00A0C1;
   color: #fff;
}

/* ===== Form action buttons - custom (bypass Bootstrap --bs-btn-border-radius var) ===== */
.block-btn-clear {
   padding: 10px 24px;
   font-size: 1rem;
   font-weight: 600;
   border-radius: 8px;
   border: 1.5px solid #fd7e14;
   background: transparent;
   color: #fd7e14;
   cursor: pointer;
   white-space: nowrap;
   transition: all 0.2s;
}

.block-btn-clear:hover {
   background: #fd7e14;
   color: #fff;
}

.block-btn-create {
   padding: 10px 40px;
   font-size: 1rem;
   font-weight: 600;
   border-radius: 8px;
   border: none;
   background: #00A0C1;
   color: #fff;
   cursor: pointer;
   white-space: nowrap;
   transition: all 0.2s;
}

.block-btn-create:hover:not(:disabled) {
   background: #008aaa;
}

.block-btn-create:disabled {
   opacity: 0.65;
   cursor: not-allowed;
}

/* ===== Inline Product Panel (BDM-style) ===== */
.product-panel {
   position: absolute;
   z-index: 100;
   left: 0;
   right: 0;
   margin-top: 10px;
   border: 1px solid #e9ecef;
   border-radius: 12px;
   background: #fff;
   box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
   overflow: hidden;
}

.product-panel-header {
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 12px 16px;
   border-bottom: 1px solid #e9ecef;
}

.product-panel-title {
   font-size: 14px;
   font-weight: 600;
   color: #435666;
}

.product-panel-close {
   font-size: 22px;
   color: #999;
   cursor: pointer;
   line-height: 1;
   padding: 0 4px;
}

.product-panel-close:hover {
   color: #333;
}

.product-panel-tabs {
   display: flex;
   border-bottom: 1px solid #e9ecef;
}

.product-panel-tab {
   flex: 1;
   padding: 8px;
   text-align: center;
   font-size: 13px;
   font-weight: 600;
   color: #435666;
   background: #f8f9fa;
   cursor: pointer;
   transition: all 0.2s;
   border-bottom: 2px solid transparent;
}

.product-panel-tab.active {
   background: #fff;
   color: #00A0C1;
   border-bottom-color: #00A0C1;
}

.product-panel-tab:hover:not(.active) {
   background: #e9ecef;
}

.product-panel-search {
   padding: 8px 12px;
   border-bottom: 1px solid #e9ecef;
}

.product-panel-search input {
   width: 100%;
   padding: 6px 12px;
   border: 1px solid #dee2e6;
   border-radius: 8px;
   font-size: 13px;
   outline: none;
   color: #435666;
}

.product-panel-search input:focus {
   border-color: #00A0C1;
}

.product-panel-list {
   overflow-y: auto;
   max-height: 220px;
   display: flex;
   flex-direction: column;
   gap: 6px;
   padding: 8px 10px;
}

.product-panel-row {
   display: flex;
   align-items: center;
   background: #fff;
   border-radius: 8px;
   border: 1px solid #e9ecef;
   overflow: hidden;
   cursor: pointer;
   transition: all 0.2s;
   flex-shrink: 0;
}

.product-panel-row:hover {
   box-shadow: 0 1px 6px rgba(0, 0, 0, 0.07);
   border-color: #00A0C1;
}

.product-panel-row-badge {
   background: #435666;
   color: #fff;
   min-width: 70px;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 10px 6px;
   font-weight: 700;
   font-size: 0.75rem;
   text-align: center;
   word-break: break-all;
}

.product-panel-row-badge.uuid {
   background: #00A0C1;
   font-size: 0.7rem;
}

.product-panel-row-info {
   flex-grow: 1;
   padding: 8px 12px;
}

.product-panel-row-name {
   font-weight: 600;
   color: #333;
   font-size: 13px;
}

.product-panel-row-action {
   color: #00A0C1;
   font-weight: 600;
   font-size: 12px;
   padding-right: 12px;
   cursor: pointer;
   white-space: nowrap;
}

.product-panel-row-action:hover {
   text-decoration: underline;
}

.product-panel-empty {
   text-align: center;
   padding: 20px;
   color: #999;
   font-size: 13px;
}

.product-panel-row-selected {
   border-color: #00A0C1;
   background: #f0fafc;
}

.product-panel-row-selected .product-panel-row-action {
   color: #28a745;
}

.selected-products-tags {
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
   margin-bottom: 8px;
   max-height: 80px;
   overflow-y: auto;
   padding: 4px 0;
}

.product-tag {
   display: inline-flex;
   align-items: center;
   gap: 4px;
   background: #e8f7fa;
   color: #435666;
   border: 1px solid #b8e6f0;
   border-radius: 16px;
   padding: 4px 10px;
   font-size: 12px;
   font-weight: 600;
}

.product-tag-remove {
   cursor: pointer;
   color: #999;
   font-size: 16px;
   line-height: 1;
   margin-left: 2px;
}

.product-tag-remove:hover {
   color: #dc3545;
}

/* --- Color Swatch Picker --- */
.color-swatch-row {
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
   margin-top: 6px;
   align-items: center;
}

.color-swatch {
   width: 28px;
   height: 28px;
   border-radius: 50%;
   border: 2px solid transparent;
   cursor: pointer;
   display: flex;
   align-items: center;
   justify-content: center;
   transition: all 0.2s ease;
   padding: 0;
   outline: none;
   box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}

.color-swatch:hover {
   transform: scale(1.15);
   box-shadow: 0 2px 8px rgba(0,0,0,0.25);
}

.color-swatch-active {
   border-color: #333;
   transform: scale(1.15);
   box-shadow: 0 0 0 2px #fff, 0 0 0 4px #333;
}

.color-swatch-custom {
   background: linear-gradient(135deg, #f8f9fa, #e9ecef) !important;
   border: 2px dashed #adb5bd;
}

.color-swatch-custom:hover {
   border-color: #00A0C1;
   background: linear-gradient(135deg, #e8f7fa, #d4f1f7) !important;
}

.color-preview-strip {
   margin-top: 8px;
   height: 28px;
   border-radius: 8px;
   transition: background-color 0.3s ease;
   position: relative;
   box-shadow: inset 0 1px 3px rgba(0,0,0,0.15);
}

.color-preview-label {
   position: absolute;
   right: 8px;
   top: 50%;
   transform: translateY(-50%);
   font-size: 12px;
   color: #fff;
   font-family: monospace;
   font-weight: 600;
   text-shadow: 0 1px 2px rgba(0,0,0,0.4);
}
</style>